import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { copyToClipboard } from '@ipfabric/ui-components/utils/copyToClipboard';
import { sleep } from '@ipfabric/ui-components/utils/sleep';
import type {
  TablesUrlPostRequestBody,
  TablesUrlPostResponseBody,
} from '@ipfabric/utils';
import type { ApiTableCommandFactory } from 'components/ApiTable/model/ApiTableModel';

export const TABLE_SHARED_VIEW_QUERY_PARAMETER = 'copyId';

type ShareTableViewCommand = {
  includeSnapshot: boolean;
  shortUrl: boolean;
};

export const shareTableView: ApiTableCommandFactory<ShareTableViewCommand> =
  (cmdContext) => async (command) => {
    const state = cmdContext.getState();
    const { webPath } = state.endpoint;
    if (!webPath) {
      stumble('Share view not supported by a table without webPath');
      return;
    }

    let tableSettingsUrlPart: string;
    if (command.shortUrl) {
      const tableSettings = cmdContext.getTableModel().settings;

      const response = await cmdContext.api.request<
        TablesUrlPostResponseBody,
        TablesUrlPostRequestBody
      >({
        method: 'POST',
        url: '/tables/url',
        // View parameters that are applied without data reload (e.g. column visibility) are used directly from state,
        // because they may have changed & updated view since last request (e.g. when hiding a column).
        // Parameters manually submitted (e.g. filters) are used from last request, because when you change but
        // do not submit filters, you want the link receiver to see the same data as you see, without applying
        // some non-submitted filters.
        data: {
          autoSizedColumns:
            cmdContext.getTableModel().settings.autoSizedColumns,
          columnVisibility:
            cmdContext.getTableModel().settings.columnVisibility,
          columnWidth: cmdContext.getTableModel().settings.columnWidth,
          filters: state.lastRequestPayload?.filters,
          orderedColumns: tableSettings.orderedColumns,
          selectedFilter: state.selectedFilterPreset?.id,
          sort: state.lastRequestPayload?.sort,
          webEndpoint: webPath,
        },
      });
      tableSettingsUrlPart = `${TABLE_SHARED_VIEW_QUERY_PARAMETER}=${encodeURIComponent(
        response.id,
      )}`;
    } else {
      tableSettingsUrlPart = `options=${encodeURIComponent(
        JSON.stringify({
          filters: state.lastRequestPayload?.filters,
        }),
      )}`;
    }

    // Clipboard API requires a direct user gesture (like onClick).
    // However, we first generate the link asynchronously from context,
    // and only then need to copy it. Calling copyToClipboard directly
    // after await would usually lose the user activation in some browsers.
    // Using `await sleep(0)` preserves the user gesture in most modern browsers,
    // making it a pragmatic workaround — though not guaranteed by the spec.
    await sleep(0);
    await copyToClipboard(
      `${window.location.origin}${webPath}?${tableSettingsUrlPart}${
        command.includeSnapshot && state.snapshotId
          ? `&selectSnapshot=${encodeURIComponent(state.snapshotId)}`
          : ''
      }`,
    );

    cmdContext.notifications.showNotification({
      message: cmdContext.msg.apiTable.urlForView.copiedNotification,
      type: 'success',
    });
  };
